import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import clsx from 'clsx';

export type SelectOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

type SelectFieldProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<SelectOption<T>>;
  id?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
};

/**
 * Custom select built on Headless UI's Listbox. Replaces the native
 * `<select>` whose dropdown overlaps the trigger on desktop and detaches into
 * an OS picker on mobile — Listbox renders an anchored, portaled panel that
 * behaves identically across platforms, inside PWA standalone mode, and the
 * Capacitor WebView.
 */
export function SelectField<T extends string>({
  value,
  onChange,
  options,
  id,
  disabled,
  className,
  placeholder,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: SelectFieldProps<T>) {
  const selected = options.find((o) => o.value === value);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className={clsx('relative', className)}>
        <ListboxButton
          id={id}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          className="input flex items-center justify-between gap-2 pr-9 text-left disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className={clsx('block truncate', !selected && 'text-slate-400')}>
            {selected?.label ?? placeholder ?? ' '}
          </span>
          <ChevronDown
            className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden
          />
        </ListboxButton>
        <ListboxOptions
          anchor={{ to: 'bottom start', gap: 4 }}
          transition
          className="z-50 w-[var(--button-width)] origin-top overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-base shadow-lg ring-1 ring-black/5 outline-none transition duration-100 ease-out empty:invisible focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
        >
          {options.map((option) => (
            <ListboxOption
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className="group flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-slate-900 select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[focus]:bg-brand-50 data-[focus]:text-brand-900"
            >
              <span className="block truncate">{option.label}</span>
              <Check
                className="hidden h-4 w-4 text-brand-600 group-data-[selected]:block"
                aria-hidden
              />
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}
